import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Icon } from './Icon.tsx';

export interface TokenOption<T extends string> {
  value: T;
  icon: string;
  name: string; // the token's full name, under its symbol
  balance?: ReactNode;
}

/**
 * The token of an amount box, picked from a menu: the chip shows the chosen token with a chevron; it opens a list of
 * every token with its icon, name and the wallet's balance. A listbox driven from its button (as the language menu):
 * arrows move, Enter/Space choose, Escape or Tab close, a click outside closes.
 */
export function TokenSelect<T extends string>({ value, options, onChange, label }: { value: T; options: TokenOption<T>[]; onChange(v: T): void; label: string }) {
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const current = Math.max(0, options.findIndex((o) => o.value === value));
  const chosen = options[current];
  const id = `tok-${label.replace(/\W+/g, '-').toLowerCase()}`;

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    window.addEventListener('mousedown', close);
    return () => window.removeEventListener('mousedown', close);
  }, [open]);

  const show = () => {
    setFocus(current);
    setOpen(true);
  };
  const choose = (v: T) => {
    onChange(v);
    setOpen(false);
  };
  const onKey = (e: KeyboardEvent) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      show();
      return;
    }
    if (!open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setFocus((f) => (f + 1) % options.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocus((f) => (f - 1 + options.length) % options.length);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      choose(options[focus].value);
    } else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div className="token-select" ref={ref}>
      <button
        type="button"
        className="token-chip token-btn"
        aria-label={`${label}: ${chosen?.value ?? ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-activedescendant={open ? `${id}-${options[focus]?.value}` : undefined}
        onClick={() => (open ? setOpen(false) : show())}
        onKeyDown={onKey}
      >
        {chosen ? <img src={chosen.icon} alt="" /> : null}
        {chosen?.value}
        <Icon name="chevron" className={open ? 'flip' : undefined} />
      </button>
      {open ? (
        <ul className="token-list" role="listbox" aria-label={label}>
          {options.map((o, i) => (
            <li
              key={o.value}
              id={`${id}-${o.value}`}
              role="option"
              aria-selected={o.value === value}
              className={i === focus ? 'focus' : undefined}
              onMouseEnter={() => setFocus(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => choose(o.value)}
            >
              <img src={o.icon} alt="" />
              <span className="tok-name">
                <b>{o.value}</b>
                <small>{o.name}</small>
              </span>
              <span className="tok-bal num">{o.balance ?? ''}</span>
              {o.value === value ? <Icon name="check" /> : <span className="tok-check" />}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
